import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Info, TrendingUp, TrendingDown, Minus } from "lucide-react";
import type { StockAsset, EtfAsset } from "@/schemas";
import { formatLargeCurrency } from "@/lib/formatters";

interface KeyMetricsCardProps {
  asset: StockAsset | EtfAsset;
}

interface MetricItemProps {
  label: string;
  value: string;
  tooltip?: string;
  trend?: "up" | "down" | "neutral";
}

function MetricItem({ label, value, tooltip, trend }: MetricItemProps) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
      <div className="flex items-center gap-1">
        <span className="text-sm text-muted-foreground">{label}</span>
        {tooltip && (
          <TooltipProvider delayDuration={100}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-3 w-3 text-muted-foreground/60 cursor-help" />
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-[250px] text-xs">
                {tooltip}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
      <div className="flex items-center gap-1">
        <span className="font-medium text-sm">{value}</span>
        {trend === "up" && <TrendingUp className="h-3 w-3 text-emerald-500" />}
        {trend === "down" && <TrendingDown className="h-3 w-3 text-red-500" />}
        {trend === "neutral" && <Minus className="h-3 w-3 text-muted-foreground" />}
      </div>
    </div>
  );
}

const formatPercent = (value?: number): string => {
  if (value === undefined || value === null) return "-";
  return `${(value * 100).toFixed(2)}%`;
};

const formatNumber = (value?: number, decimals = 2): string => {
  if (value === undefined || value === null) return "-";
  return value.toFixed(decimals);
};

export function KeyMetricsCard({ asset }: KeyMetricsCardProps) {
  return (
    <Card className="h-full">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium">Key Metrics</CardTitle>
      </CardHeader>
      <CardContent className="space-y-0">
        <MetricItem
          label="Market Cap"
          value={formatLargeCurrency(asset.market_cap, asset.currency_symbol)}
          tooltip="Total market value of all outstanding shares"
        />
        <MetricItem
          label="P/E (TTM)"
          value={formatNumber(asset.trailing_pe)}
          tooltip="Price-to-Earnings ratio. Lower values may indicate undervaluation. Compare with industry peers."
        />
        <MetricItem
          label="P/E (Forward)"
          value={formatNumber(asset.forward_pe)}
          tooltip="Forward P/E uses estimated future earnings. Lower than TTM suggests expected earnings growth."
        />
        <MetricItem
          label="P/S"
          value={formatNumber(asset.price_to_sales)}
          tooltip="Price-to-Sales ratio. Useful for unprofitable companies. Lower = potentially undervalued."
        />
        <MetricItem
          label="P/B"
          value={formatNumber(asset.price_to_book)}
          tooltip="Price-to-Book ratio. Below 1 may indicate undervaluation, but check asset quality."
        />
        <MetricItem
          label="PEG Ratio"
          value={formatNumber((asset as StockAsset).peg_ratio)}
          tooltip="Price/Earnings to Growth ratio. Below 1 suggests undervaluation relative to growth rate."
        />
        <MetricItem
          label="Dividend Yield"
          value={formatPercent(asset.dividend_yield)}
          tooltip="Annual dividend as percentage of stock price"
          trend={asset.dividend_yield && asset.dividend_yield > 0.02 ? "up" : undefined}
        />
        <MetricItem
          label="Beta"
          value={formatNumber(asset.beta)}
          tooltip="Volatility relative to market. β>1 = more volatile, β<1 = less volatile"
        />
        <MetricItem
          label="ROE"
          value={formatPercent(asset.return_on_equity)}
          tooltip="Return on Equity. Measures profitability relative to shareholder equity. Higher is better."
          trend={asset.return_on_equity && asset.return_on_equity > 0.15 ? "up" : undefined}
        />
        <MetricItem
          label="Profit Margin"
          value={formatPercent(asset.profit_margins)}
          tooltip="Net income as percentage of revenue. Higher margins indicate better profitability."
          trend={asset.profit_margins && asset.profit_margins > 0.1 ? "up" : undefined}
        />
      </CardContent>
    </Card>
  );
}
